import { Box, ModalDialog, Stack } from '@front10/base-elements';
import { useIsMobileView, useTranslation } from '@front10/utils';
import { useButton } from '@react-aria/button';
import { useDateFormatter } from '@react-aria/i18n';
import { OverlayContainer, useOverlayPosition, useOverlayTrigger } from '@react-aria/overlays';
import { useOverlayTriggerState } from '@react-stately/overlays';
import parseISO from 'date-fns/parseISO';
import startOfToday from 'date-fns/startOfToday';
import dayjs from 'dayjs';
import React, { useMemo, useRef, useState } from 'react';
import { EffectsMap, useEffectReducer } from 'use-effect-reducer';
import { MobileHeader } from '../base-elements/MobileHeader';
import { isDayBefore } from '../../helpers/date';
import { PrimaryFilterButton } from '../base-elements/PrimaryFilterButton';
import CalendarContent from './atoms/CalendarContent';
import CalendarFooter from './atoms/CalendarFooter';
import CalendarHeader from './atoms/CalendarHeader';
import {
  generateMonthList,
  getAddDays,
  isDateOutOfView,
  isDayBetween,
  isSameDay,
  subMonthsNumber
} from './atoms/helper';
import { CalendarProvider, useEffectOnlyOnce } from './atoms/hooks';
import WeekDayNames from './atoms/WeekDayNames';
import { useLocalizationData } from './DatePickerLocalization';
import { dateRangeReducer } from './dateRangeReducer';
import { useBlackoutDates } from './i18n';
import { Popover } from './Popover';
import {
  CommonDateProps,
  DateEvent,
  DateModifier,
  DateRange,
  DayData,
  EffectSingleDateRangePicker,
  EventSingleDateRangePicker,
  StateSingleDateRangePicker
} from './types';

export interface OnChangeDataEvent {
  value?: Date;
}

//Defining State

//Defining props
type SingleDateRangePickerProps = Partial<
  CommonDateProps & {
    localizationData?: Record<string, $TSFixMe>;
    /**
     * Calendar settings
     */
    calendarProps: any;
    /**
     * Default settings for both inputs
     */
    inputItem: any;
    /**
     * Default settings for left input
     */
    leftInputItem: any;
    /**
     * Default settings for right input
     */
    rightInputItem: any;

    /**
     * default value of date
     */
    defaultValue: string[];

    /**
     * onClear ranges
     */
    onClear: Function;
    /**
     * onChange function
     */
    onChange: (event: { dates: Date[] }) => void;
    /*
     * onInputChange function
     */
    onInputChange: Function;

    /**
     * Patter to format date
     */
    focusReferences: Function;
    /**
     * function fired when focus the component
     */
    onFocus: (event: $TSFixMe, component: 'start' | 'end') => void;

    /**
     * Placeholder of the departure date
     */
    departInputPlaceholderLabelKey: string;
    /**
     * PLaceholder of the return date
     */
    returnInputPlaceholderLabelKey: string;
    /**
     * Close icon
     */
    iconClose: string;
    /**
     * Label key of input.
     */
    labelKey: string;
    /**
     * Label key of departure input.
     */
    departureLabelKey: string;
    /**
     * Label key of return input.
     */
    returnLabelKey: string;
    /**
     * Show label of input or not
     */
    showLabel: boolean;
    /**
     * Label Settings
     */
    textLabel: any;
    /**
     * clear button settings
     */
    clearButton: any;
    /*
     * navigationItem settings
     */
    navigationItem: any;
    showYearCaption: boolean;
    isMobile: boolean;
    border?: boolean;
    style: any;
    availableAtoms: Array<string>;
    showWeekDayNames?: boolean;
    setTopAtoms?: Array<string>;
    weekDaysContainer?: any;
    calendarContainer?: any;
    calendarMonthContainer?: any;
    callToAction?: any;
    topAtomsContainer?: any;
    footer: {
      elementsToShow: Array<string>;
      labelKeyInformation?: string;
    };
    departure: {
      start: number;
      end: number;
    };
    toggleElement: any;
    openPopupOnKeyDown?: boolean;
    openPopupOnClick?: boolean;
    events?: DateEvent[];
  }
>;

export type SingleDateRangePickerProps_ = SingleDateRangePickerProps & { label: string };

const cellWidth = 'calc(13vw - 2px)';

const formatOption = { month: 'short' } as const;

function SingleDateRangePicker({
  allowedCharacters = '[0-9A-Za-z ]',
  availableDays = 45,
  captionItem = {},
  calendarProps = {
    position: 'relative'
  },
  showYearCaption = true,
  clearButton = {},
  departInputPlaceholderLabelKey = 'cmp-label--datepicker-depart',
  returnInputPlaceholderLabelKey = 'cmp-label--datepicker-return',
  footerCheckboxComponent = {},
  footerContainerComponent = {},
  openPopupOnClick = true,
  openPopupOnKeyDown = true,
  callToAction = {},
  footerSelectComponent = {},
  footerDoneButtonComponent = {},
  footerClearButtonComponent = {},
  showWeekDayNames = true,
  format = 'MM/DD/YYYY',
  focusReferences = () => {},
  iconClose = 'close',
  invalidClass = 'fr-input-invalid',
  inputDateScreenReader = 'MMMM D, YYYY',
  labelKey = 'cmp-date-range-selector-label',
  departureLabelKey = 'cmp-date-range-departure-label',
  returnLabelKey = 'cmp-date-range-return-label',
  footerInformationTextComponent,
  modifiersColors = {
    '--cellWidth': cellWidth,
    '[data-fr-day]': {
      width: [cellWidth, '42px'],
      height: [cellWidth, '42px'],
      fontSize: ['calc(0.8em + 1vw)', '1em'],
      borderRadius: '5px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '.25em',
      paddingX: '8px',
      paddingTop: '.5em',
      paddingBottom: '.12em'
    },
    '[data-fr-disabled-day]': {
      color: 'disabledDayColor'
    },
    '[data-fr-day]:focus': {
      outline: 'auto var(--color-secondary) 2px'
    },
    '[data-fr-day]:focus:not(:focus-visible)': {
      outline: 'none'
    },
    '> [data-fr-selected-day]': {
      bg: 'colorPrimary',
      color: 'white'
    },
    '[data-fr-today-day]': {
      fontWeight: 'bold'
    },
    '[data-fr-past-day]': {
      color: 'colorBackgroundSoft'
    },
    '[data-fr-blackout-day]': {
      color: 'disabledDayColor'
    }
  },
  navigationItem = { marginWidth: '1' },
  navColor = 'link',
  numberOfMonths: configuredNumberOfMonths = 2,
  onClear = () => {},
  onChange = () => {},
  onFocus = () => {},
  onInputChange = () => {},
  onValueClass = 'fr-value-selected',
  popover = null,
  renderWeekdayElement = null,
  renderNavbarElement = null,
  showInOneColumn = false,
  showLabel = false,
  showRemainingDays = false,
  leftInputItem = {},
  rightInputItem = {},
  border = false,
  inputItem = {
    closePopoverAfterAction: false
  },
  textLabel = {},
  rightIcon = {},
  rightIconOpen = {},
  defaultValue = [],
  showSeparator = true,
  dayItems = {},
  weekdayItem = {
    weekDayFormat: 'short',
    fontWeight: 'bold',
    border: false
  },
  departure = {
    start: 0,
    end: 180
  },
  blackoutDays = [],
  blackoutDaysFormat = format,
  availableAtoms = ['CalendarHeader', 'Calendar'],
  footer = {
    elementsToShow: [],
    labelKeyInformation: 'cmp-datePicker-text-information'
  },
  weekDaysContainer,
  calendarContainer,
  calendarMonthContainer,
  setTopAtoms = ['InputDate', 'WeekDayNames'],
  toggleElement,
  topAtomsContainer,
  ...props
}: SingleDateRangePickerProps_) {
  let isMobile = useIsMobileView();
  if (props.isMobile) {
    isMobile = true;
  }

  const initialDate = useMemo(() => {
    return dayjs()
      .add(departure?.start ?? 0, 'day')
      .toDate();
  }, [departure?.start]);

  const numberOfMonths = useMemo(() => {
    return configuredNumberOfMonths;
  }, [configuredNumberOfMonths]);

  const blackoutDates = useBlackoutDates(blackoutDays, blackoutDaysFormat);
  const [defaultDates] = useState(() => {
    return defaultValue.map((date) => parseISO(date).getTime());
  });
  const initialState: () => StateSingleDateRangePicker = () => {
    const today = startOfToday();
    const defaultDateTime = defaultDates[defaultDates.length - 1];
    return {
      selectedComponent: 'start',
      displayDate: initialDate,
      availableDays: departure ? departure.start + departure.end : availableDays,
      months: [],
      today,
      currentDate: new Set(defaultDates),
      preSelected: defaultDateTime ? new Date(defaultDateTime) : initialDate,
      selectedRange: new Set(defaultDates),
      popoverOpen: false,
      valid: true,
      focusSelectedDay: false
    };
  };
  const reduceEffectMap: EffectsMap<
    StateSingleDateRangePicker,
    EventSingleDateRangePicker,
    EffectSingleDateRangePicker
  > = {
    CHANGE_CURRENT_MONTH: (state) => {},
    SET_DISPLAY_DATE_ACTION: (state) => {
      const lastDate = getAddDays(
        state.today,
        departure?.end ? departure?.start + departure?.end - 1 : 0
      );
      const isEnabledNextMonth = isDateOutOfView(state.displayDate, numberOfMonths, lastDate);

      const months = generateMonthList({
        displayDate: isEnabledNextMonth
          ? state.displayDate
          : !isMobile
          ? dayjs(state.displayDate).get('month') === dayjs(lastDate).get('month') &&
            numberOfMonths === 1
            ? state.displayDate
            : subMonthsNumber(state.displayDate, 1)
          : state.displayDate,
        modifiers,
        modifiersColors,
        numberOfMonths: numberOfMonths,
        blackOutDays: blackoutDates,
        showRemainingDays,
        locale: localizationData
      });

      dispatch({ type: 'SET_MONTHS', payload: months });

      if (!isMobile && !isEnabledNextMonth && numberOfMonths > 1) {
        dispatch({
          type: 'SET_DISPLAY_DATE_ACTION',
          payload: subMonthsNumber(state.displayDate, 1)
        });
      }
    },
    SET_DATE_ACTION: () => {},
    CHANGE_DATES: () => {
      sendOnChangeEvent();
      overlayState.close();
    },
    CLEAR_ACTION: () => {
      onClear();
    },
    CANCEL_ACTION: (state) => {
      overlayState.close();
    },
    SET_PRESELECTED_DATE: (state, action) => {},
    SELECT_DAY_ACTION: (state, action) => {},
    CLOSE_POPOVER_ACTION: () => {
      overlayState.close();
    },
    OPEN_POPOVER_ACTION: () => {
      overlayState.open();
    }
  };

  const [state, dispatch] = useEffectReducer<
    StateSingleDateRangePicker,
    EventSingleDateRangePicker,
    EffectSingleDateRangePicker
  >(dateRangeReducer, initialState, reduceEffectMap);

  //refs
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);
  let overlayState = useOverlayTriggerState({
    onOpenChange: (isOpen) => {
      if (isOpen) {
        handleOpenPopover();
      }
    }
  });
  // Get popover positioning props relative to the trigger
  let { overlayProps: positionProps } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef: popoverRef,
    placement: 'bottom left',
    offset: 0,
    isOpen: overlayState.isOpen
  });

  let overlayTriggerState = { ...overlayState, close: () => {} };

  let { triggerProps } = useOverlayTrigger({ type: 'dialog' }, overlayTriggerState, triggerRef);

  // useButton ensures that focus management is handled correctly,
  // across all browsers. Focus is restored to the button once the
  // popover closes.
  let { buttonProps } = useButton(
    {
      onPress: () => overlayState.open(),
      elementType: 'div'
    },
    triggerRef
  );

  const leftInputRef = useRef<HTMLElement>(null);
  const rightInputRef = useRef<HTMLElement>(null);
  const containerRef = useRef<any>(null);

  const translate = useTranslation();
  const localizationData = useLocalizationData();

  useEffectOnlyOnce(() => {
    dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: initialDate });
  }, [dispatch]);

  //set modifiers to be used by datePicker
  const modifiers: Partial<DateModifier> = {
    today: (day: Date) => isSameDay(day, state.today),
    beginRange: (day: Date) => false,
    endRange: (day) => false,
    selected: (day) => state.selectedRange.has(day.getTime()),
    past: (day) => isDayBefore(day, state.today),
    disabled: {
      before:
        departure && departure.start
          ? dayjs(state.today).add(departure.start, 'day').toDate()
          : state.today,
      after:
        departure && departure.end && departure.start
          ? dayjs(state.today)
              .add(departure.end + departure.start - 1, 'day')
              .toDate()
          : getAddDays(state.today, state.availableDays - 1)
    },
    blackout: (date) => {
      return blackoutDates.some((current: DateRange) => {
        if (current.start && current.end) {
          return isDayBetween(date, current?.start, current?.end, true);
        }
        return false;
      });
    },
    preSelected: (day) => isSameDay(day, state.preSelected)
  };

  //input events

  const handleDayClick = (dayData: DayData) => {
    dispatch({ type: 'SET_PRESELECTED_DATE', payload: dayData.date });

    dispatch({ type: 'SELECT_DAY_ACTION', payload: dayData.date, modifiers: dayData.modifiers });
  };

  //popover events
  const handleClosePopover = () => {
    overlayState.close();
    dispatch({ type: 'SET_POPOVER', payload: { popoverOpen: false, focusSelectedDay: false } });
    if (state.selectedComponent === 'start') {
      leftInputRef?.current?.focus();
    } else {
      rightInputRef?.current?.focus();
    }
  };
  const handleOpenPopover = () => {
    dispatch({ type: 'SET_POPOVER', payload: { popoverOpen: true, focusSelectedDay: false } });

    // dispatch({ type: 'SET_PREVIOUS_DATE', payload: state.currentDate });

    dispatch({
      type: 'SET_DATE_ACTION',
      payload: state.currentDate,
      component: 'start',
      close: false
    });

    if (!state.preSelected && state.months.length > 0) {
      dispatch({
        type: 'SET_PRESELECTED_DATE',
        payload: state.months[0].firstAvailableDay()
      });
    }
    dispatch({ type: 'SET_FOCUS_SELECTED_DAY', payload: true });
  };
  //component events
  const sendOnChangeEvent = () => {
    const { currentDate } = state;
    const dates = Array.from(currentDate)
      .sort()
      .map((date) => new Date(date));

    onChange({
      dates
    });
  };

  const elements: Record<string, any> = {
    CalendarHeader: (
      <CalendarHeader
        {...navigationItem}
        dispatch={dispatch}
        state={state}
        format={format}
        currentMonth={state.currentMonth}
      />
    ),
    Calendar: (
      <CalendarContent
        key={'calendar-content-key'}
        numberOfMonths={numberOfMonths}
        showYear={showYearCaption}
        months={state.months}
        captionItem={captionItem}
        weekdayItem={weekdayItem}
        dayItems={dayItems}
        onDayClick={handleDayClick}
        showWeekDayNames={showWeekDayNames}
        weekDaysContainer={weekDaysContainer}
        calendarContainer={calendarContainer}
        calendarMonthContainer={calendarMonthContainer}
        currentDate={JSON.stringify(state.currentDate) === '{}' ? null : state.currentDate}
        displayDate={state.displayDate}
        dispatch={dispatch}
        initialDate={initialDate}
        events={props.events}
      />
    ),
    Footer: (
      <CalendarFooter
        footerCheckboxComponent={footerCheckboxComponent}
        footerSelectComponent={footerSelectComponent}
        footerClearButtonComponent={footerClearButtonComponent}
        footerDoneButtonComponent={footerDoneButtonComponent}
        footerContainerComponent={footerContainerComponent}
        dispatch={dispatch}
        state={state}
        footerSettings={footer}
        firstAvailableDay={departure.start ? departure.start : 0}
      />
    )
  };

  const mobileElements: Record<string, any> = {
    WeekDayNames: (
      <WeekDayNames weekdayItem={weekdayItem} weekDaysContainer={weekDaysContainer}></WeekDayNames>
    ),
    Calendar: (
      <CalendarContent
        isMobile={true}
        showYear={showYearCaption}
        ref={containerRef}
        numberOfMonths={numberOfMonths}
        months={state.months}
        displayDate={state.displayDate}
        captionItem={captionItem}
        weekdayItem={weekdayItem}
        dayItems={dayItems}
        onDayClick={handleDayClick}
        showWeekDayNames={showWeekDayNames}
        weekDaysContainer={weekDaysContainer}
        calendarContainer={calendarContainer}
        calendarMonthContainer={calendarMonthContainer}
        currentDate={JSON.stringify(state.currentDate) === '{}' ? null : state.currentDate}
        preSelectedDate={state.preSelected}
        initialDate={initialDate}
        events={props.events}
      />
    ),
    Footer: (
      <CalendarFooter
        footerCheckboxComponent={footerCheckboxComponent}
        footerSelectComponent={footerSelectComponent}
        footerClearButtonComponent={footerClearButtonComponent}
        footerDoneButtonComponent={footerDoneButtonComponent}
        footerContainerComponent={footerContainerComponent}
        callToAction={callToAction}
        dispatch={dispatch}
        state={state}
        footerSettings={footer}
        footerInformationTextComponent={footerInformationTextComponent}
        isMobile={true}
        format={format}
        firstAvailableDay={departure.start ? departure.start : 0}
      />
    )
  };
  const temporalAvailableAtoms = [...availableAtoms];
  const getTopAtom = () =>
    temporalAvailableAtoms.filter((e) => setTopAtoms?.find((element) => element === e));
  const removeTopAtom = () => temporalAvailableAtoms.filter((e) => setTopAtoms?.indexOf(e) === -1);
  const footerPosition = temporalAvailableAtoms.indexOf('Footer');
  const newAvailableAtoms =
    footerPosition !== -1
      ? temporalAvailableAtoms.splice(footerPosition, 1)
      : temporalAvailableAtoms;

  const memoState = useMemo(
    () => ({
      ...state,
      popoverOpen: overlayState.isOpen
    }),
    [state, overlayState.isOpen]
  );

  const formatter = useDateFormatter(formatOption);
  const [formattedSavedValue, count] = useMemo(() => {
    let formattedSavedValue = 'cmp-date-selector-label-any';
    let count = undefined;
    if (state.currentDate.size !== 0) {
      const dates = Array.from(state.currentDate)
        .sort()
        .map((time) => new Date(time));
      const selectedDates = dates.length > 6 ? dates.slice(0, 6) : dates;
      formattedSavedValue =
        formatter.format(dates[0]) + ' ' + selectedDates.map((date) => date.getDate()).join(', ');
      if (dates.length > 6) {
        count = dates.length;
      }
      return [formattedSavedValue, count];
    }

    return [translate(formattedSavedValue, 'Any'), count];
  }, [formatter, state.currentDate, translate]);

  const button = (
    <PrimaryFilterButton
      label={translate(props.label, props.label)}
      value={formattedSavedValue}
      count={count}
      {...buttonProps}
      {...triggerProps}
      ref={triggerRef}
    />
  );

  const content = (
    <>
      {button}
      {overlayState.isOpen ? (
        <OverlayContainer>
          {isMobile ? (
            <ModalDialog isOpen size="stretch" onClose={handleClosePopover}>
              <Stack sx={{ height: '100%' }}>
                <MobileHeader
                  onBackButtonPress={handleClosePopover}
                  title={translate('cmp-date-range-picker-header-title', 'Date range')}
                  variant={'fontSize9'}
                />
                <div>
                  {setTopAtoms && (
                    <Box {...topAtomsContainer}>
                      {getTopAtom().map((e: any) => mobileElements[e])}
                    </Box>
                  )}
                  {setTopAtoms
                    ? removeTopAtom().map((e) => mobileElements[e])
                    : availableAtoms && newAvailableAtoms.map((e) => mobileElements[e])}
                </div>
                <Box mt="auto">{footer.elementsToShow ? mobileElements['Footer'] : null}</Box>
              </Stack>
            </ModalDialog>
          ) : (
            <Popover
              {...positionProps}
              popoverRef={popoverRef}
              isOpen={overlayState.isOpen}
              onClose={handleClosePopover}
            >
              {availableAtoms && availableAtoms.map((e) => elements[e])}
            </Popover>
          )}
        </OverlayContainer>
      ) : null}
    </>
  );

  return (
    <CalendarProvider
      modifiers={modifiers}
      modifiersColors={modifiersColors}
      state={memoState}
      dispatch={dispatch}
      format={format}
      numberOfMonths={numberOfMonths}
    >
      {content}
    </CalendarProvider>
  );
}

export default SingleDateRangePicker;
